<template>
  <SubHeading
    :home="home"
    :tabs="[]"
    :shorts="true"
    :currTab="currTab"
    color="cyan darken-3"
    @active="handleBack"
  />

  <div class="settings-route-surface">
    <nav class="sba-collection-breadcrumb settings-breadcrumb z-depth-0">
      <div class="container nav-wrapper">
        <div class="sba-collection-breadcrumb-row">
          <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
            <i class="material-icons">home</i>
            <span>Dashboard</span>
          </router-link>
          <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
          <a
            href="#!"
            class="breadcrumb sba-collection-crumb blue-grey-text pointable"
            @click.prevent="handleBack"
          >
            <span>Settings</span>
          </a>
          <template v-if="!isHome">
            <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
            <span class="breadcrumb sba-collection-crumb blue-grey-text">
              {{ currentHead }}
            </span>
          </template>
          <span class="sba-collection-context">
            System Configuration
          </span>
        </div>
      </div>
    </nav>

    <div class="container settings-page-shell sba-slide-in">
      <LoadingDiv v-if="!isReady" />

      <template v-else>
        <div class="settings-leader">
          <div>
            <span class="settings-eyebrow">System Settings</span>
            <h4>Settings</h4>
            <p>
              Manage system access, setup records, rules, plans, and tariff references.
            </p>
          </div>
          <span class="settings-leader-chip">Admin only</span>
        </div>

        <div class="settings-layout">
          <aside class="settings-sidebar">
            <button
              v-for="tab in settingTabs"
              :key="tab.id"
              class="settings-nav-item"
              :class="{ 'is-active': currTab?.id == tab.id }"
              type="button"
              @click="handleActive(tab)"
            >
              <i class="material-icons">{{ settingIcon(tab.title) }}</i>
              <span>
                <strong>{{ handleSplit(tab.title, "head") }}</strong>
              </span>
            </button>
          </aside>

          <main class="settings-content" :key="settingsSurfaceKey">
            <div class="settings-content-head">
              <span class="settings-eyebrow">{{ currentHead }}</span>
              <h5>{{ currentTitle }}</h5>
              <p>{{ currentDescription }}</p>
            </div>

          <div class="settings-info-panel" v-if="isSystemInfo">
            <section class="settings-activity-console">
              <div class="settings-activity-topline">
                <div>
                  <span class="settings-eyebrow">System Activity</span>
                  <h5>Critical Action Updates</h5>
                  <p>
                    Track high-impact actions such as new clients, providers, invoice approvals,
                    tariff changes, access updates, and bulk operations.
                  </p>
                </div>
                <span class="settings-live-pill">
                  <i class="material-icons">fiber_manual_record</i>
                  Active
                </span>
              </div>

              <div class="settings-activity-summary">
                <span>{{ activityPages?.total || 0 }} logged actions</span>
                <span>{{ criticalCount }} critical</span>
                <span>{{ importantCount }} important</span>
                <span>Refreshes every 15 mins</span>
              </div>

              <div class="settings-log-card">
                <div class="settings-log-head">
                  <div>
                    <span class="settings-eyebrow">Activity Feed</span>
                    <h5>Latest Critical Actions</h5>
                  </div>
                  <span>{{ lastActivityRefresh }}</span>
                </div>

                <form class="settings-log-filters" @submit.prevent="handleActivitySearch">
                  <label>
                    <span>Search activity</span>
                    <input
                      v-model.trim="activitySearch"
                      type="search"
                      placeholder="Client, provider, invoice, user..."
                      autocomplete="off"
                    />
                  </label>
                  <label>
                    <span>Group</span>
                    <select v-model="activityGroup" class="browser-default">
                      <option value="all">All groups</option>
                      <option value="client">Clients</option>
                      <option value="provider">Providers</option>
                      <option value="member">Members</option>
                      <option value="principal">Principals</option>
                      <option value="invoice">Invoices</option>
                      <option value="payment">Payments</option>
                      <option value="claim_batch">Claims</option>
                      <option value="tariff">Tariffs</option>
                      <option value="plan">Plans</option>
                      <option value="policy">Policies</option>
                      <option value="user">Users</option>
                    </select>
                  </label>
                  <label>
                    <span>Severity</span>
                    <select v-model="activitySeverity" class="browser-default">
                      <option value="all">All levels</option>
                      <option value="critical">Critical</option>
                      <option value="important">Important</option>
                      <option value="notice">Notice</option>
                      <option value="info">Info</option>
                    </select>
                  </label>
                  <label>
                    <span>Time</span>
                    <select v-model="activityTime" class="browser-default">
                      <option value="all">All time</option>
                      <option value="today">Today</option>
                      <option value="week">Last 7 days</option>
                      <option value="month">Last 30 days</option>
                    </select>
                  </label>
                  <button class="settings-log-search" type="submit" :disabled="activityLoading">
                    <i class="material-icons">{{ activityLoading ? "hourglass_empty" : "search" }}</i>
                    <span>{{ activityLoading ? "Loading" : "Search" }}</span>
                  </button>
                </form>

                <div class="progress settings-log-progress" v-if="activityLoading">
                  <div class="indeterminate"></div>
                </div>

                <div class="settings-log-message is-error" v-if="activityError">
                  {{ activityError }}
                </div>

                <div class="settings-log-message" v-else-if="!activityRows.length && !activityLoading">
                  No activity logs found for the current filters.
                </div>

                <div
                  v-for="item in activityRows"
                  :key="item.id"
                  class="settings-log-row"
                  :class="`settings-log-row-${item.tone}`"
                >
                  <i class="material-icons">{{ item.icon }}</i>
                  <div>
                    <strong>{{ item.title }}</strong>
                    <small>{{ item.description }}</small>
                  </div>
                  <em>{{ item.state }}</em>
                </div>

                <div class="settings-log-more" v-if="hasMoreActivity">
                  <button
                    type="button"
                    class="settings-log-load-more"
                    :disabled="activityLoadingMore"
                    @click="loadMoreActivityLogs"
                  >
                    <i class="material-icons">{{ activityLoadingMore ? "hourglass_empty" : "expand_more" }}</i>
                    <span>{{ activityLoadingMore ? "Loading" : "Load more" }}</span>
                  </button>
                </div>
              </div>
            </section>
          </div>

          <div class="settings-empty-panel" v-else-if="isHome">
            <i class="material-icons">tune</i>
            <div>
              <h5>Choose a settings area</h5>
              <p>
                Use the left panel to open the exact setup area you want to
                review. Changes here affect shared system behaviour, so this
                page stays intentionally plain and direct.
              </p>
            </div>
          </div>

          <section class="settings-module-panel" v-else>
            <HmoSetup
              v-if="currTab?.title == 'HMO -> General Information'"
              :key="settingsModuleKey"
              @next="handleNext"
            />
            <UserSetup
              v-if="currTab?.title == 'Users -> Access Control'"
              :key="settingsModuleKey"
              @next="handleNext"
            />
            <SystemModuleSetup
              v-if="currTab?.title == 'Modules -> Availability'"
              :key="settingsModuleKey"
              @next="handleNext"
            />
            <SchemeSetup
              v-if="currTab?.title == 'Schemes -> Billing Controls'"
              :key="settingsModuleKey"
              :showSchemeControls="true"
              @next="handleNext"
            />
            <PlanSetup
              v-if="currTab?.title == 'Plans -> Shelf'"
              :key="settingsModuleKey"
              @next="handleNext"
            />
            <TariffSetup
              v-if="currTab?.title == 'Tariffs -> Standard'"
              :key="settingsModuleKey"
              @next="handleNext"
            />
            <SettingSetup
              v-if="currTab?.title == 'Rules -> Default'"
              :key="settingsModuleKey"
              @next="handleNext"
            />
          </section>
          </main>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useStore } from "vuex";
import useModuleNavigation from "@/composables/useModuleNavigation";
import SubHeading from "@/components/HeaderSubDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import HmoSetup from "@/components/setup/HmoSetupComponent.vue";
import UserSetup from "@/components/setup/UserSetupComponent.vue";
import SystemModuleSetup from "@/components/setup/SystemModuleSetupComponent.vue";
import SchemeSetup from "@/components/setup/SchemeSetupComponent.vue";
import PlanSetup from "@/components/setup/PlanSetupComponent.vue";
import TariffSetup from "@/components/setup/TariffSetupComponent.vue";
import SettingSetup from "@/components/setup/SettingSetupComponent.vue";
import getItems from "@/composables/getItems";

export default {
  name: "settingsHome",
  components: {
    SubHeading,
    LoadingDiv,
    HmoSetup,
    UserSetup,
    SystemModuleSetup,
    SchemeSetup,
    PlanSetup,
    TariffSetup,
    SettingSetup,
  },

  setup() {
    const store = useStore();
    const { activateLocalTab, scrollToModuleTop } = useModuleNavigation();
    const home = ref(null);
    const isReady = ref(true);
    const error = ref(null);
    const currTab = ref(null);
    const settingsModuleKey = ref(0);
    const tabs = ref([]);
    const activityLogs = ref([]);
    const activityPages = ref(null);
    const activityLoading = ref(false);
    const activityLoadingMore = ref(false);
    const activityError = ref(null);
    const activitySearch = ref("");
    const activityTime = ref("all");
    const activityGroup = ref("all");
    const activitySeverity = ref("all");
    const activityPage = ref(1);
    const lastActivityRefresh = ref("Not loaded");
    const activityRefreshTimer = ref(null);
    const activityRequestInFlight = ref(false);

    const settingMeta = {
      Home: {
        icon: "dashboard",
        title: "Settings Home",
        description:
          "Open a configuration area from the left panel. This page is for controlled system setup, not day-to-day transactions.",
      },
      "System Info -> Status & Audit": {
        icon: "monitor_heart",
        title: "System Info",
        description:
          "Check whether the application is active and review important administrative actions in one place.",
      },
      "HMO -> General Information": {
        icon: "business",
        title: "HMO Profile",
        description:
          "Maintain the core HMO information used across official records and operational documents.",
      },
      "Users -> Access Control": {
        icon: "admin_panel_settings",
        title: "User Access",
        description:
          "Manage administrative access paths and user control settings for internal teams.",
      },
      "Modules -> Availability": {
        icon: "apps",
        title: "Module Availability",
        description:
          "Control the installation-level modules that can be assigned to users and used across the system.",
      },
      "Schemes -> Billing Controls": {
        icon: "account_tree",
        title: "Scheme Billing Controls",
        description:
          "Control provider-created billing batches and dormant professional fee settings for schemes.",
      },
      "Plans -> Shelf": {
        icon: "inventory_2",
        title: "Plan Shelf",
        description:
          "Review and maintain benefit plans available for client policy setup.",
      },
      "Tariffs -> Standard": {
        icon: "receipt_long",
        title: "Standard Tariffs",
        description:
          "Manage standard pricing references used by provider and claim workflows.",
      },
      "Rules -> Default": {
        icon: "rule",
        title: "Default Rules",
        description:
          "Control default policy rules that guide family size, age limits, and operational checks.",
      },
    };

    const settingTabs = computed(() =>
      tabs.value.filter((tab) => {
        if (tab.title.toLowerCase().includes("home")) {
          return false;
        }

        if (
          ["Modules -> Availability", "Schemes -> Billing Controls"].includes(tab.title) &&
          !isMaster.value
        ) {
          return false;
        }

        return true;
      })
    );

    const isMaster = computed(() => !!store.state.user?.is_master);

    const isHome = computed(() =>
      currTab.value?.title?.toLowerCase()?.includes("home")
    );

    const isSystemInfo = computed(() =>
      currTab.value?.title == "System Info -> Status & Audit"
    );

    const settingsSurfaceKey = computed(() => `${currTab.value?.id || "home"}-${settingsModuleKey.value}`);

    const activeMeta = computed(
      () => settingMeta[currTab.value?.title] || settingMeta.Home
    );

    const currentHead = computed(() =>
      isHome.value ? "Settings" : handleSplit(currTab.value?.title, "head")
    );

    const currentTitle = computed(() => activeMeta.value.title);

    const currentDescription = computed(() => activeMeta.value.description);

    const activityRows = computed(() =>
      activityLogs.value.map((item) => ({
        id: item.id,
        icon: activityIcon(item),
        title: item.title || "System activity",
        description: item.display_summary || item.summary || item.event_key,
        state: formatActivityTime(item.created_at),
        tone: activityTone(item),
      }))
    );

    const criticalCount = computed(() =>
      activityLogs.value.filter((item) => item.severity == "critical").length
    );

    const importantCount = computed(() =>
      activityLogs.value.filter((item) => item.severity == "important").length
    );

    const hasMoreActivity = computed(() => {
      if (!activityPages.value) {
        return false;
      }

      return activityPages.value.current_page < activityPages.value.last_page;
    });

    // start
    onMounted(() => {
      // page name
      document.title = "Settings | EasyHMO";
    });

    // handle back
    const handleBack = () => {
      settingsModuleKey.value++;
      // set default
      currTab.value = tabs.value.find((x) => x.title.includes("System Info"));
      scrollToModuleTop();
    };

    // handle back
    const handleNext = () => {
      // call reset
      handleBack();
    };

    // handle split
    const handleSplit = (val, type) => {
      let newVal;
      let parts;
      const value = val || "";
      //set
      if (value.includes("->")) {
        parts = value.split("->").map((s) => s.trim());
      } else {
        parts = [value, null];
      }
      // close
      if (type == "head") {
        newVal = parts[0];
      } else if (type == "body") {
        newVal = parts[1];
      }
      // return
      return newVal || "";
    };

    const settingIcon = (title) => settingMeta[title]?.icon || "settings";

    const buildActivityUrl = (page = 1) => {
      const params = ["per_page=25", `page=${page}`];

      if (activitySearch.value) {
        params.push(`q=${encodeURIComponent(activitySearch.value)}`);
      }

      if (activityTime.value) {
        params.push(`time=${activityTime.value}`);
      }

      if (activityGroup.value) {
        params.push(`group=${activityGroup.value}`);
      }

      if (activitySeverity.value) {
        params.push(`severity=${activitySeverity.value}`);
      }

      return `activity/logs?${params.join("&")}`;
    };

    const loadActivityLogs = async ({ silent = false, page = 1, append = false } = {}) => {
      if (!isSystemInfo.value) {
        return;
      }

      if (document.hidden || activityRequestInFlight.value) {
        return;
      }

      activityRequestInFlight.value = true;

      if (append) {
        activityLoadingMore.value = true;
      } else if (!silent) {
        activityLoading.value = true;
      }

      activityError.value = null;

      try {
        const { res, err } = await getItems(buildActivityUrl(page));

        if (err.value) {
          activityError.value = err.value;
        } else {
          const payload = res.value?.data;
          activityPages.value = payload;
          activityLogs.value = append
            ? [...activityLogs.value, ...(payload?.data || [])]
            : payload?.data || [];
          activityPage.value = payload?.current_page || page;
          lastActivityRefresh.value = `Updated ${new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}`;
        }
      } finally {
        activityLoading.value = false;
        activityLoadingMore.value = false;
        activityRequestInFlight.value = false;
      }
    };

    const handleActivitySearch = () => {
      activityPage.value = 1;
      loadActivityLogs();
    };

    const loadMoreActivityLogs = () => {
      if (!hasMoreActivity.value || activityLoadingMore.value) {
        return;
      }

      loadActivityLogs({
        page: activityPage.value + 1,
        append: true,
      });
    };

    const startActivityRefresh = () => {
      stopActivityRefresh();
      activityRefreshTimer.value = setInterval(() => {
        if (!document.hidden) {
          loadActivityLogs({ silent: true });
        }
      }, 15 * 60 * 1000);
    };

    const stopActivityRefresh = () => {
      if (activityRefreshTimer.value) {
        clearInterval(activityRefreshTimer.value);
        activityRefreshTimer.value = null;
      }
    };

    const activityIcon = (item) => {
      const icons = {
        client: "business",
        policy: "event_available",
        plan: "inventory_2",
        principal: "person_add",
        member: "badge",
        provider: "local_hospital",
        schedule: "view_timeline",
        tariff: "receipt_long",
        claim_batch: "fact_check",
        invoice: "request_quote",
        payment: "payments",
        repayment: "currency_exchange",
        user: "verified_user",
      };

      return icons[item.event_group] || "bolt";
    };

    const activityTone = (item) => {
      if (item.severity == "critical") {
        return "warning";
      }

      const tones = {
        client: "client",
        policy: "change",
        plan: "change",
        principal: "enrollee",
        member: "enrollee",
        provider: "provider",
        schedule: "tariff",
        tariff: "tariff",
        claim_batch: "access",
        invoice: "payment",
        payment: "payment",
        repayment: "payment",
        user: "access",
      };

      return tones[item.event_group] || "upload";
    };

    const formatActivityTime = (value) => {
      if (!value) {
        return "";
      }

      const date = new Date(value);
      const diff = Date.now() - date.getTime();
      const minute = 60 * 1000;
      const hour = 60 * minute;
      const day = 24 * hour;

      if (diff < minute) {
        return "Just now";
      }

      if (diff < hour) {
        return `${Math.floor(diff / minute)} mins ago`;
      }

      if (diff < day) {
        return `${Math.floor(diff / hour)} hrs ago`;
      }

      if (diff < day * 7) {
        return `${Math.floor(diff / day)} days ago`;
      }

      return date.toLocaleDateString();
    };

    // Initiate
    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Settings", title: "Home" });
      tabs.value.push({
        id: 2,
        name: "Settings",
        title: "System Info -> Status & Audit",
      });
      tabs.value.push({
        id: 3,
        name: "Settings",
        title: "HMO -> General Information",
      });
      tabs.value.push({
        id: 4,
        name: "Settings",
        title: "Users -> Access Control",
      });
      if (isMaster.value) {
        tabs.value.push({
          id: 5,
          name: "Settings",
          title: "Modules -> Availability",
        });
        tabs.value.push({
          id: 9,
          name: "Settings",
          title: "Schemes -> Billing Controls",
        });
      }
      tabs.value.push({ id: 6, name: "Settings", title: "Plans -> Shelf" });
      tabs.value.push({
        id: 7,
        name: "Settings",
        title: "Tariffs -> Standard",
      });
      tabs.value.push({ id: 8, name: "Settings", title: "Rules -> Default" });
      // set default
      currTab.value = tabs.value.find((x) => x.title.includes("System Info"));
      // set home
      home.value = tabs.value.find((x) => x.title.includes("Home"));
      // load activity
      loadActivityLogs();
      startActivityRefresh();
    });

    watch([activityTime, activityGroup, activitySeverity], () => {
      activityPage.value = 1;
      loadActivityLogs();
    });

    watch(isSystemInfo, (active) => {
      if (active) {
        loadActivityLogs();
        startActivityRefresh();
      } else {
        stopActivityRefresh();
      }
    });

    onUnmounted(() => {
      stopActivityRefresh();
    });

    // handle curr status
    const handleActive = (tab) => {
      settingsModuleKey.value++;
      activateLocalTab(tab, currTab);
    };

    return {
      handleSplit,
      handleActive,
      handleNext,
      handleBack,
      settingMeta,
      settingIcon,
      settingTabs,
      activityRows,
      activityPages,
      activityLoading,
      activityLoadingMore,
      activityError,
      activitySearch,
      activityTime,
      activityGroup,
      activitySeverity,
      hasMoreActivity,
      lastActivityRefresh,
      criticalCount,
      importantCount,
      handleActivitySearch,
      loadMoreActivityLogs,
      currentDescription,
      currentHead,
      currentTitle,
      isHome,
      isSystemInfo,
      settingsSurfaceKey,
      home,
      isReady,
      error,
      currTab,
      settingsModuleKey,
      tabs,
    };
  },
};
</script>

<style scoped>
:deep(.sub-nav-mobile) {
  display: none !important;
}

.settings-page-shell {
  margin-top: -6px;
  padding-bottom: 56px;
  position: relative;
  z-index: 1;
}

.settings-layout {
  align-items: start;
  display: grid;
  gap: 14px;
  grid-template-columns: 1fr;
}

.settings-leader,
.settings-sidebar,
.settings-content,
.settings-content-head,
.settings-info-panel,
.settings-empty-panel,
.settings-module-panel,
.settings-activity-console,
.settings-log-card,
.settings-log-row,
.settings-module-panel :deep(form),
.settings-module-panel :deep(.card),
.settings-module-panel :deep(.card-panel) {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.settings-leader {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e3e8eb;
  border-radius: 6px;
  box-shadow: 0 14px 26px -28px rgba(38, 50, 56, 0.72);
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
  padding: 20px 24px;
}

.settings-leader h4 {
  color: #263238;
  font-size: 1.72rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0;
}

.settings-leader p {
  color: #607d8b;
  font-size: 0.96rem;
  line-height: 1.48;
  margin: 7px 0 0;
  max-width: 720px;
}

.settings-leader-chip {
  background: #eef4f5;
  border: 1px solid #d6e1e4;
  border-radius: 999px;
  color: #263238;
  flex: 0 0 auto;
  font-size: 0.86rem;
  font-weight: 800;
  padding: 10px 16px;
}

.settings-sidebar,
.settings-content {
  background: #ffffff;
  border: 1px solid #e3e8eb;
  border-radius: 6px;
  box-shadow: 0 14px 28px -30px rgba(38, 50, 56, 0.72);
}

.settings-sidebar {
  display: grid;
  gap: 8px;
  grid-template-columns: 230px repeat(auto-fit, minmax(168px, 1fr));
  overflow: hidden;
  padding: 10px;
}

.settings-sidebar-head {
  align-self: stretch;
  background: #f7f9f9;
  border: 1px solid #edf1f3;
  border-radius: 5px;
  padding: 14px 16px;
}

.settings-eyebrow {
  color: #00897b;
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0;
  margin-bottom: 8px;
}

.settings-sidebar-head h6,
.settings-content-head h5,
.settings-empty-panel h5 {
  color: #263238;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0;
}

.settings-sidebar-head h6 {
  font-size: 1.05rem;
}

.settings-content-head h5 {
  font-size: 1.42rem;
}

.settings-sidebar-head p,
.settings-content-head p,
.settings-empty-panel p {
  color: #607d8b;
  font-size: 0.92rem;
  line-height: 1.48;
  margin: 7px 0 0;
}

.settings-nav-item {
  align-items: center;
  background: #fbfcfc;
  border: 1px solid #edf1f3;
  border-radius: 5px;
  color: #263238;
  cursor: pointer;
  display: flex;
  gap: 13px;
  min-height: 64px;
  padding: 12px 14px;
  text-align: left;
  width: 100%;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.settings-nav-item:last-child {
  border-bottom: 1px solid #edf1f3;
}

.settings-nav-item:hover {
  background: #f1f8f7;
  border-color: #9bd2cb;
  box-shadow: 0 10px 20px -24px rgba(0, 105, 92, 0.8);
  transform: translateY(-1px);
}

.settings-nav-item.is-active {
  background: #263238;
  color: #ffffff;
}

.settings-nav-item i {
  align-items: center;
  background: #eef4f5;
  border-radius: 5px;
  color: #00897b;
  display: inline-flex;
  flex: 0 0 36px;
  font-size: 20px;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.settings-nav-item.is-active i {
  background: rgba(255, 255, 255, 0.14);
  color: #ffffff;
}

.settings-nav-item strong,
.settings-nav-item small {
  display: block;
}

.settings-nav-item strong {
  font-size: 0.98rem;
  font-weight: 800;
  letter-spacing: 0;
}

.settings-nav-item small {
  color: #78909c;
  font-size: 0.82rem;
  font-weight: 700;
  margin-top: 3px;
}

.settings-nav-item.is-active small {
  color: rgba(255, 255, 255, 0.72);
}

.settings-content {
  min-width: 0;
  overflow: hidden;
}

.settings-content-head {
  background: #fbfcfc;
  border-bottom: 1px solid #edf1f3;
  padding: 20px 24px;
}

.settings-empty-panel {
  align-items: center;
  display: flex;
  gap: 18px;
  padding: 34px 32px;
}

.settings-info-panel {
  padding: 18px 22px 24px;
}

.settings-system-state {
  align-items: center;
  background: #fbfcfc;
  border: 1px solid #e3e8eb;
  border-radius: 6px;
  display: grid;
  gap: 18px;
  grid-template-columns: 170px minmax(0, 1fr) 160px;
  margin-bottom: 14px;
  padding: 18px;
}

.settings-system-pulse {
  align-items: center;
  background: #ffffff;
  border: 1px solid #dce5e8;
  border-radius: 6px;
  display: flex;
  gap: 12px;
  min-height: 74px;
  padding: 18px;
}

.settings-system-pulse span {
  background: #00897b;
  border-radius: 50%;
  box-shadow: 0 0 0 7px rgba(0, 137, 123, 0.12);
  display: inline-block;
  height: 13px;
  width: 13px;
}

.settings-system-pulse strong,
.settings-system-copy h5,
.settings-system-meta strong {
  color: #263238;
  font-weight: 800;
  letter-spacing: 0;
}

.settings-system-copy h5 {
  font-size: 1.28rem;
  margin: 0;
}

.settings-system-copy p {
  color: #607d8b;
  font-size: 0.98rem;
  line-height: 1.6;
  margin: 9px 0 0;
}

.settings-system-meta {
  border-left: 1px solid #e3e8eb;
  padding-left: 20px;
}

.settings-system-meta small,
.settings-system-meta em {
  color: #607d8b;
  display: block;
  font-size: 0.8rem;
  font-style: normal;
  font-weight: 700;
}

.settings-system-meta strong {
  display: block;
  font-size: 1rem;
  margin: 7px 0;
}

.settings-area-board {
  background: #ffffff;
  border: 1px solid #e3e8eb;
  border-radius: 6px;
  overflow: hidden;
}

.settings-area-head {
  align-items: center;
  background: #fbfcfc;
  border-bottom: 1px solid #edf1f3;
  display: flex;
  justify-content: space-between;
  padding: 20px 22px;
}

.settings-area-head h5 {
  color: #263238;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0;
}

.settings-area-head > span {
  background: #eef4f5;
  border: 1px solid #d6e1e4;
  border-radius: 999px;
  color: #263238;
  font-size: 0.78rem;
  font-weight: 800;
  padding: 8px 12px;
}

.settings-area-row {
  align-items: flex-start;
  background: #ffffff;
  border: 0;
  border-bottom: 1px solid #edf1f3;
  cursor: pointer;
  display: grid;
  gap: 14px;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  padding: 16px 22px;
  text-align: left;
  width: 100%;
}

.settings-area-row:last-child {
  border-bottom: 0;
}

.settings-area-row:hover {
  background: #f7fafb;
}

.settings-area-row i {
  align-items: center;
  background: #eef4f5;
  border: 1px solid #dce5e8;
  border-radius: 5px;
  color: #00897b;
  display: inline-flex;
  font-size: 20px;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.settings-area-row strong,
.settings-area-row small {
  display: block;
}

.settings-area-row strong {
  color: #263238;
  font-size: 0.98rem;
  font-weight: 800;
}

.settings-area-row small {
  color: #607d8b;
  font-size: 0.9rem;
  line-height: 1.42;
  margin-top: 4px;
}

.settings-area-row em {
  background: #f5f7f8;
  border: 1px solid #e3e8eb;
  border-radius: 999px;
  color: #607d8b;
  font-size: 0.76rem;
  font-style: normal;
  font-weight: 800;
  padding: 7px 10px;
  white-space: nowrap;
}

.settings-log-row {
  align-items: flex-start;
  border-bottom: 1px solid #edf1f3;
  display: grid;
  gap: 14px;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  position: relative;
  padding: 16px 22px;
}

.settings-log-row:last-child {
  border-bottom: 0;
}

.settings-log-row::before {
  background: #dce5e8;
  bottom: -1px;
  content: "";
  left: 43px;
  position: absolute;
  top: 58px;
  width: 1px;
}

.settings-log-row:last-child::before {
  display: none;
}

.settings-log-row i {
  align-items: center;
  background: #ffffff;
  border: 1px solid #dce5e8;
  border-radius: 50%;
  color: #00897b;
  display: inline-flex;
  font-size: 19px;
  height: 42px;
  justify-content: center;
  width: 42px;
  z-index: 1;
}

.settings-log-row strong,
.settings-log-row small {
  display: block;
}

.settings-log-row strong {
  color: #263238;
  font-size: 0.98rem;
  font-weight: 800;
}

.settings-log-row small {
  color: #607d8b;
  font-size: 0.9rem;
  margin-top: 4px;
}

.settings-log-row em {
  background: #f5f7f8;
  border: 1px solid #e3e8eb;
  border-radius: 999px;
  color: #607d8b;
  font-size: 0.76rem;
  font-style: normal;
  font-weight: 800;
  padding: 7px 10px;
  white-space: nowrap;
}

.settings-log-row-client i,
.settings-log-row-client em {
  background: #e8f5e9;
  border-color: #c8e6c9;
  color: #2e7d32;
}

.settings-log-row-provider i,
.settings-log-row-provider em {
  background: #e3f2fd;
  border-color: #bbdefb;
  color: #1565c0;
}

.settings-log-row-tariff i,
.settings-log-row-tariff em {
  background: #fff8e1;
  border-color: #ffe082;
  color: #8a5a00;
}

.settings-log-row-payment i,
.settings-log-row-payment em {
  background: #e0f2f1;
  border-color: #b2dfdb;
  color: #00695c;
}

.settings-log-row-enrollee i,
.settings-log-row-enrollee em {
  background: #ede7f6;
  border-color: #d1c4e9;
  color: #512da8;
}

.settings-log-row-change i,
.settings-log-row-change em {
  background: #e8eaf6;
  border-color: #c5cae9;
  color: #303f9f;
}

.settings-log-row-warning i,
.settings-log-row-warning em {
  background: #ffebee;
  border-color: #ffcdd2;
  color: #b71c1c;
}

.settings-log-row-restore i,
.settings-log-row-restore em {
  background: #f1f8e9;
  border-color: #dcedc8;
  color: #558b2f;
}

.settings-log-row-access i,
.settings-log-row-access em {
  background: #eceff1;
  border-color: #cfd8dc;
  color: #37474f;
}

.settings-log-row-upload i,
.settings-log-row-upload em {
  background: #fff3e0;
  border-color: #ffcc80;
  color: #e65100;
}

.settings-empty-panel i {
  align-items: center;
  background: #eef4f5;
  border-radius: 6px;
  color: #00897b;
  display: inline-flex;
  flex: 0 0 52px;
  font-size: 28px;
  height: 52px;
  justify-content: center;
  width: 52px;
}

.settings-module-panel {
  overflow-x: auto;
  padding: 22px;
}

@media only screen and (max-width: 992px) {
  .settings-page-shell {
    margin-top: 0;
  }

  .settings-leader {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
  }

  .settings-layout {
    grid-template-columns: 1fr;
  }

  .settings-system-state {
    grid-template-columns: 1fr;
  }

  .settings-system-meta {
    border-left: 0;
    border-top: 1px solid #e3e8eb;
    padding-left: 0;
    padding-top: 16px;
  }

  .settings-sidebar {
    position: static;
  }
}

@media only screen and (max-width: 600px) {
  .settings-content-head,
  .settings-empty-panel,
  .settings-info-panel,
  .settings-module-panel {
    padding-left: 18px;
    padding-right: 18px;
  }

  .settings-empty-panel {
    align-items: flex-start;
    flex-direction: column;
  }

  .settings-log-head,
  .settings-log-row {
    align-items: flex-start;
    grid-template-columns: 1fr;
  }
}

/* Minimal settings skin */
.settings-page-shell {
  background: #f8fafb;
  border: 2px solid #263238;
  border-radius: 10px;
  box-shadow: 0 22px 42px -36px rgba(38, 50, 56, 0.72);
  margin-top: -8px;
  max-width: 1120px;
  padding: 26px 28px 56px;
}

.settings-route-surface {
  background: #f8fafb;
  min-height: calc(100vh - 144px);
  padding-bottom: 72px;
}

.settings-breadcrumb {
  background: #f8fafb !important;
  background-color: #f8fafb !important;
  box-shadow: 0 10px 18px -22px rgba(38, 50, 56, 0.45);
  padding-bottom: 22px;
}

.settings-leader {
  background: transparent;
  border: 0;
  border-bottom: 2px solid #263238;
  border-radius: 0;
  box-shadow: none;
  margin-bottom: 20px;
  padding: 0 0 18px;
}

.settings-eyebrow {
  color: #607d8b;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.settings-leader h4 {
  font-size: 2.1rem;
  font-weight: 500;
  margin-top: 2px;
}

.settings-leader p {
  color: #546e7a;
  font-size: 1rem;
  line-height: 1.55;
  max-width: 520px;
}

.settings-leader-chip {
  background: transparent;
  border: 1px solid #b0bec5;
  border-radius: 999px;
  color: #37474f;
  font-size: 0.75rem;
  padding: 8px 12px;
}

.settings-layout {
  gap: 18px;
}

.settings-sidebar {
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0;
}

.settings-nav-item {
  background: transparent;
  border: 1px solid #d8e1e4;
  border-radius: 999px;
  color: #37474f;
  flex: 0 0 auto;
  gap: 8px;
  min-height: 38px;
  padding: 0 14px 0 10px;
  width: auto;
}

.settings-nav-item:last-child {
  border-bottom: 1px solid #d8e1e4;
}

.settings-nav-item:hover {
  background: #ffffff;
  border-color: #90a4ae;
  box-shadow: none;
  transform: none;
}

.settings-nav-item.is-active {
  background: #263238;
  border-color: #263238;
  color: #ffffff;
}

.settings-nav-item i {
  background: transparent;
  border-radius: 0;
  color: currentColor;
  flex: 0 0 auto;
  font-size: 17px;
  height: auto;
  width: auto;
}

.settings-nav-item.is-active i {
  background: transparent;
}

.settings-nav-item strong {
  font-size: 0.82rem;
  font-weight: 700;
}

.settings-content {
  background: #ffffff;
  border: 1px solid #dfe6e8;
  border-radius: 8px;
  box-shadow: none;
  padding: 18px;
}

.settings-content-head {
  background: #f8fafb;
  border: 1px solid #dfe6e8;
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 16px 18px;
}

.settings-content-head h5 {
  font-size: 1.45rem;
  font-weight: 500;
}

.settings-content-head p {
  max-width: 650px;
}

.settings-info-panel,
.settings-module-panel {
  padding: 0;
}

.settings-system-state,
.settings-area-board,
.settings-activity-console,
.settings-empty-panel,
.settings-module-panel :deep(form),
.settings-module-panel :deep(.card),
.settings-module-panel :deep(.card-panel) {
  background: #ffffff;
  border: 1px solid #dfe6e8;
  border-radius: 6px;
  box-shadow: none;
}

.settings-system-state {
  grid-template-columns: minmax(0, 1fr) auto;
  margin-bottom: 12px;
  padding: 18px;
}

.settings-activity-console {
  border: 0;
  padding: 0;
}

.settings-activity-topline {
  align-items: flex-start;
  border-bottom: 1px solid #dfe6e8;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  padding-bottom: 16px;
}

.settings-activity-topline h5 {
  color: #263238;
  font-size: 1.35rem;
  font-weight: 500;
  margin: 0;
}

.settings-activity-topline p {
  color: #546e7a;
  line-height: 1.5;
  margin: 8px 0 0;
  max-width: 700px;
}

.settings-live-pill {
  align-items: center;
  border: 1px solid #b0bec5;
  border-radius: 999px;
  color: #37474f;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.76rem;
  font-weight: 800;
  gap: 6px;
  padding: 7px 10px;
}

.settings-live-pill i {
  color: #00897b;
  font-size: 10px;
}

.settings-activity-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  padding: 16px 0 0;
}

.settings-activity-summary span {
  background: #f6f8f9;
  border: 1px solid #dfe6e8;
  border-radius: 999px;
  color: #455a64;
  font-size: 0.76rem;
  font-weight: 800;
  padding: 7px 10px;
}

.settings-system-pulse {
  background: transparent;
  border: 0;
  min-height: auto;
  padding: 0;
}

.settings-system-copy {
  grid-column: 1 / -1;
}

.settings-system-meta {
  border-left: 0;
  padding-left: 0;
  text-align: right;
}

.settings-area-head {
  background: transparent;
  padding: 16px 18px;
}

.settings-log-card {
  border-color: #dfe6e8;
  border-radius: 6px;
  overflow: hidden;
}

.settings-log-head {
  background: #fbfcfc;
  padding: 15px 18px;
}

.settings-log-head h5 {
  font-size: 1.05rem;
  font-weight: 500;
}

.settings-log-filters {
  align-items: end;
  border-bottom: 1px solid #dfe6e8;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(190px, 1.4fr) minmax(130px, 0.8fr) minmax(130px, 0.8fr) minmax(130px, 0.8fr) auto;
  padding: 14px 18px;
}

.settings-log-filters label {
  color: #455a64;
  display: grid;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.settings-log-filters input,
.settings-log-filters select {
  background: #ffffff;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  box-sizing: border-box;
  color: #263238;
  font-size: 0.92rem;
  height: 38px;
  margin: 0;
  padding: 0 11px;
}

.settings-log-filters input:focus,
.settings-log-filters select:focus {
  border-color: #263238;
  box-shadow: none;
  outline: 0;
}

.settings-log-search {
  align-items: center;
  background: #263238;
  border: 1px solid #263238;
  border-radius: 6px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 800;
  gap: 7px;
  height: 38px;
  justify-content: center;
  padding: 0 14px;
}

.settings-log-search:disabled {
  cursor: wait;
  opacity: 0.7;
}

.settings-log-search i {
  font-size: 17px;
}

.settings-log-progress {
  background: #edf2f4;
  border-radius: 0;
  height: 3px;
  margin: 0;
}

.settings-log-message {
  color: #607d8b;
  font-size: 0.9rem;
  font-weight: 700;
  padding: 18px;
}

.settings-log-message.is-error {
  color: #b71c1c;
}

.settings-log-more {
  border-top: 1px solid #dfe6e8;
  display: flex;
  justify-content: center;
  padding: 14px 18px 16px;
}

.settings-log-load-more {
  align-items: center;
  background: #ffffff;
  border: 1px solid #cfd8dc;
  border-radius: 999px;
  color: #263238;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.84rem;
  font-weight: 800;
  gap: 7px;
  min-height: 36px;
  padding: 0 16px;
}

.settings-log-load-more:hover {
  border-color: #263238;
}

.settings-log-load-more:disabled {
  cursor: wait;
  opacity: 0.7;
}

.settings-log-load-more i {
  font-size: 18px;
}

.settings-log-row {
  align-items: center;
  padding: 13px 18px;
}

.settings-log-row > div {
  padding-right: 28px;
}

.settings-log-row::before {
  display: none;
}

.settings-log-row i {
  border-radius: 6px;
  height: 34px;
  width: 34px;
}

.settings-log-row strong {
  font-size: 0.94rem;
}

.settings-log-row small {
  line-height: 1.4;
}

.settings-area-row {
  padding: 14px 18px;
}

.settings-area-row:hover {
  background: #fbfcfc;
}

.settings-area-row i {
  background: transparent;
  border: 0;
  border-radius: 0;
  color: #455a64;
}

.settings-area-row em {
  background: transparent;
  border-color: #cfd8dc;
}

.settings-module-panel {
  border: 0;
  overflow: visible;
}

.settings-module-panel :deep(form) {
  padding: 18px;
}

@media only screen and (min-width: 601px) and (max-width: 992px) {
  :deep(.custom-nav),
  :deep(.sub-nav-shell),
  :deep(.sub-nav-mobile) {
    display: none !important;
  }

  .settings-page-shell {
    padding: 24px 24px 50px;
    width: 92%;
  }

  .settings-layout {
    gap: 18px;
    grid-template-columns: 1fr;
  }

  .settings-leader {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
  }

  .settings-sidebar {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-width: 100%;
  }

  .settings-nav-item {
    justify-content: flex-start;
    min-height: 52px;
    padding: 0 16px;
    width: 100%;
  }

  .settings-content {
    padding: 16px;
  }

  .settings-content-head,
  .settings-module-panel :deep(form),
  .settings-module-panel :deep(.card),
  .settings-module-panel :deep(.card-panel) {
    padding: 18px;
  }

  .settings-activity-topline {
    align-items: flex-start;
    flex-direction: column;
  }

  .settings-log-filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .settings-log-search {
    width: 100%;
  }

  .settings-module-panel {
    overflow-x: auto;
  }
}

@media only screen and (max-width: 600px) {
  :deep(.custom-nav),
  :deep(.sub-nav-shell),
  :deep(.sub-nav-mobile) {
    display: none !important;
  }

  .settings-leader {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }

  .settings-page-shell {
    padding-top: 20px;
    width: 94%;
  }

  .settings-system-state,
  .settings-area-row,
  .settings-log-filters {
    grid-template-columns: 1fr;
  }

  .settings-system-meta {
    text-align: left;
  }

  .settings-leader,
  .settings-content-head,
  .settings-empty-panel,
  .settings-info-panel,
  .settings-module-panel,
  .settings-activity-console,
  .settings-log-card {
    border-radius: 6px;
  }

  .settings-leader {
    padding: 20px;
  }

  .settings-leader h4,
  .settings-content-head h5,
  .settings-activity-topline h5,
  .settings-area-head h5,
  .settings-log-head h5 {
    font-size: 1.35rem;
    line-height: 1.25;
  }

  .settings-leader-chip,
  .settings-live-pill,
  .settings-log-search,
  .settings-log-load-more {
    justify-content: center;
    width: 100%;
  }

  .settings-layout {
    gap: 14px;
  }

  .settings-sidebar {
    display: grid;
    gap: 8px;
    max-width: 100%;
    overflow-x: auto;
  }

  .settings-nav-item {
    min-height: 48px;
  }

  .settings-content-head,
  .settings-activity-console,
  .settings-empty-panel,
  .settings-module-panel {
    padding: 18px;
  }

  .settings-activity-topline,
  .settings-activity-summary,
  .settings-log-more {
    align-items: stretch;
    flex-direction: column;
  }

  .settings-activity-summary span {
    text-align: center;
  }

  .settings-log-filters {
    padding: 14px;
  }

  .settings-log-row {
    align-items: flex-start;
    grid-template-columns: 34px 1fr;
    padding: 14px;
  }

  .settings-log-row > div {
    padding-right: 0;
  }

  .settings-area-row {
    gap: 10px;
  }

  .settings-area-row em {
    justify-content: center;
    width: 100%;
  }

  .settings-module-panel {
    overflow-x: auto;
  }

  .settings-module-panel :deep(form),
  .settings-module-panel :deep(.card),
  .settings-module-panel :deep(.card-panel) {
    border-radius: 6px;
    padding: 16px;
  }

  .settings-module-panel :deep(.row) {
    margin-left: 0;
    margin-right: 0;
  }

  .settings-module-panel :deep(.col) {
    padding-left: 0;
    padding-right: 0;
  }

  .settings-module-panel :deep(.btn),
  .settings-module-panel :deep(.btn-large),
  .settings-module-panel :deep(button) {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .settings-leader,
  .settings-sidebar,
  .settings-content,
  .settings-content-head,
  .settings-info-panel,
  .settings-empty-panel,
  .settings-module-panel,
  .settings-activity-console,
  .settings-log-card,
  .settings-log-row,
  .settings-module-panel :deep(form),
  .settings-module-panel :deep(.card),
  .settings-module-panel :deep(.card-panel) {
    animation: none;
  }
}
</style>
